import { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowsMerge, ArrowRight, Tag, MapPin, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_LABELS = {
  title: "Title", caption: "Caption", description: "Description",
  primary_domain: "Category", photographer: "Photographer",
  photographer_url: "Photographer URL", location: "Location", landmark: "Landmark",
};

export default function MergeDialog({ photo, open, onOpenChange, onMerged }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && photo) {
      setPreview(null); setError(null); setLoading(true);
      api.get(`/photos/${photo.id}/merge-preview`)
        .then((r) => setPreview(r.data))
        .catch((e) => setError(e?.response?.data?.detail || "Could not load merge preview"))
        .finally(() => setLoading(false));
    }
  }, [open, photo]);

  const fmt = (f, v) => {
    if (f === "location") return [v.city, v.country].filter(Boolean).join(", ") || "location";
    if (f === "landmark") return v?.name || "landmark";
    return String(v);
  };

  const doMerge = async () => {
    setMerging(true);
    try {
      const r = await api.post(`/photos/${photo.id}/merge`);
      toast.success(`Merged into original${r.data.added_tags?.length ? ` · +${r.data.added_tags.length} tags` : ""}`);
      onMerged?.(photo.id, r.data.merged_into, r.data.original);
      onOpenChange(false);
    } catch {
      toast.error("Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const changes = preview?.changes || {};
  const changeKeys = Object.keys(changes);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ArrowsMerge size={20} weight="bold" className="text-amber-600" /> Smart Duplicate Merge
          </DialogTitle>
          <DialogDescription>
            Bring the duplicate's tags and any missing metadata into the original, then delete the duplicate.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="py-6 text-center text-sm text-muted-foreground">Loading preview…</p>}
        {error && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-600">
            <WarningCircle size={18} weight="bold" /> {error}
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <div className="text-center">
                <img src={imageUrl(preview.duplicate.id)} alt="duplicate"
                  className="h-24 w-24 rounded-md object-cover ring-2 ring-red-300" />
                <p className="mt-1 text-[11px] font-semibold uppercase text-red-500">Duplicate</p>
              </div>
              <ArrowRight size={22} className="text-amber-600" weight="bold" />
              <div className="text-center">
                <img src={imageUrl(preview.original.id)} alt="original"
                  className="h-24 w-24 rounded-md object-cover ring-2 ring-emerald-400" />
                <p className="mt-1 text-[11px] font-semibold uppercase text-emerald-600">Original (kept)</p>
              </div>
            </div>

            <div className="rounded-md border border-border bg-zinc-50 p-3" data-testid="merge-changes">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-500">Will be merged into original</p>
              {changeKeys.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing new to merge — original already has all this data. The duplicate will just be deleted.</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {changeKeys.map((f) => (
                    <li key={f} className="flex items-start gap-2" data-testid={`merge-field-${f}`}>
                      {f === "manual_tags" ? <Tag size={15} className="mt-0.5 text-[#F27A54]" weight="fill" />
                        : f === "location" ? <MapPin size={15} className="mt-0.5 text-[#F27A54]" weight="fill" />
                        : <ArrowRight size={13} className="mt-1 text-zinc-400" />}
                      <span>
                        <span className="font-semibold">{FIELD_LABELS[f] || f}:</span>{" "}
                        {f === "manual_tags"
                          ? <span className="text-zinc-600">{(changes[f] || []).join(", ")}</span>
                          : <span className="text-zinc-600">{fmt(f, changes[f])}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100">
            Cancel
          </button>
          <button data-testid="merge-confirm" onClick={doMerge} disabled={merging || loading || !!error}
            className="flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">
            <ArrowsMerge size={15} weight="bold" /> {merging ? "Merging…" : "Merge & delete duplicate"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
